/**
 * A bought line's deliveries, one row per despatch (orders' #2748; supplier
 * interface spec 05 §4): what went and when, the carrier with its tracking
 * link (a free-text carrier by name, or "Carrier not recorded"), and the
 * delivery's *Record delivered* / *Record received* while some of it has not
 * arrived. Shown under the line on the order page's Delivery step and on the
 * purchase order page's Lines. The words are shared-core's
 * (`despatch-progress.ts`); the steps are the view's.
 */
import type {
  DeliveryLineView,
  LineDespatchProgress,
  LineDespatchRow,
} from "@shared-core/data/order-to-invoice-types";
import {
  deliveryCarrier,
  deliveryRecordLabel,
  deliverySent,
} from "@shared-core/domain/despatch-progress";
import { Button } from "../../primitives/button";
import { InfoTip } from "../../patterns/InfoTip";

/** The delivery line a delivery's step records against, offering just that delivery's part. */
export function deliveryLineForRow(
  row: LineDespatchRow,
  deliveryLines: DeliveryLineView[],
): DeliveryLineView | null {
  const record = row.record;
  if (!record) return null;
  const line = deliveryLines.find((d) => d.id === record.delivery_line_id);
  if (!line) return null;
  return {
    ...line,
    outstanding_quantity: record.quantity,
    record_delivered_url:
      record.kind === "record_delivered" ? record.url : null,
    record_received_url: record.kind === "record_received" ? record.url : null,
  };
}

export function LineDespatches({
  progress,
  deliveryLines,
  busy,
  onRequestRecord,
}: {
  progress: LineDespatchProgress;
  deliveryLines: DeliveryLineView[];
  busy: boolean;
  /** Open the delivery's step; without it the rows are read only. */
  onRequestRecord?: (line: DeliveryLineView) => void;
}) {
  if (progress.deliveries.length === 0) return null;
  return (
    <ul className="space-y-1 text-sm" aria-label="Deliveries">
      {progress.deliveries.map((row) => {
        const recordLine = deliveryLineForRow(row, deliveryLines);
        const label = deliveryRecordLabel(row);
        return (
          <li
            key={row.despatch_no}
            className="flex flex-wrap items-center gap-x-2 gap-y-1"
          >
            <span className="tabular-nums">{deliverySent(row)}</span>
            <span className="text-muted-foreground">·</span>
            <span>
              {deliveryCarrier(row)}
              {row.tracking_number &&
                (row.tracking_url ? (
                  <a
                    href={row.tracking_url}
                    target="_blank"
                    rel="noreferrer"
                    className="ml-1 tabular-nums underline"
                  >
                    {row.tracking_number}
                  </a>
                ) : (
                  <span className="ml-1 tabular-nums text-muted-foreground">
                    {row.tracking_number}
                  </span>
                ))}
            </span>
            <span className="text-xs text-muted-foreground">
              {row.is_arrived ? "Arrived" : row.tracking_label}
            </span>
            {recordLine && label && onRequestRecord && (
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={busy}
                onClick={() => onRequestRecord(recordLine)}
              >
                {label}
              </Button>
            )}
            {!row.record && row.record_received_blocked_reason && (
              <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                Record received
                <InfoTip label="Why Record received isn't offered">
                  {row.record_received_blocked_reason}
                </InfoTip>
              </span>
            )}
          </li>
        );
      })}
    </ul>
  );
}
